// Objective → value-bridge shared contract (conversational-bridge lane, Deliverable A).
// Generic, metric-agnostic bridge picture: finance anchor (baseline → target → gap), priced
// lever package with receipted contributions, honestly-withheld levers, and de-confliction
// over a CLOSED overlap-dimension vocabulary. Types only + pure validation functions —
// runtime assembly lives in the API lane, never here.
import type { NumericBinding } from './numeric-binding';
import type {
  GovernedToolOutputEvidenceRef,
  ImpactClaim,
} from './governed-tool-output-envelope';

// ---------------------------------------------------------------------------
// Target metric registry
// ---------------------------------------------------------------------------

// Lever branch ids are strings, not an enum: the repo has three disconnected string
// catalogs for lever branches today. Do not mint a fourth — alias and move on.
export type LeverBranchId = string;

export type TargetMetricFamily =
  | 'cost'
  | 'revenue'
  | 'cash'
  | 'returns'
  | 'margin'
  | 'capacity';

export type TargetMetricUnit = 'USD' | 'PCT' | 'BPS' | 'COUNT';

export type TargetMetricDirection = 'increase' | 'decrease' | 'maintain';

export type TargetMetricDirectionMeaning =
  | 'improves_objective'
  | 'worsens_objective'
  | 'contextual';

export type TargetMetricAskVsAnchorPolicy =
  | 'same_metric_required'
  | 'anchorable_substitution_allowed'
  | 'block_if_not_anchorable';

export type TargetMetricRegistryEntry = Readonly<{
  metricId: string;
  label: string;
  metricFamily: TargetMetricFamily;
  unit: TargetMetricUnit;
  // Direction semantics are registry data, never inferred from phrasing: "reduce cost"
  // and "increase margin" both improve the objective only because the registry says so.
  directionSemantics: Readonly<{
    increaseMeans: TargetMetricDirectionMeaning;
    decreaseMeans: TargetMetricDirectionMeaning;
    allowedDirections: readonly TargetMetricDirection[];
  }>;
  // Where the governed baseline comes from — anchor module + intent + formula + evidence.
  baselineSource: Readonly<{
    anchorModuleId: string;
    anchorIntentId: string;
    formulaRef: string;
    requiredEvidenceKey: string;
  }>;
  allowedLeverBranches: readonly LeverBranchId[];
  allowedConstraints: readonly ObjectiveConstraintId[];
  defaultAnchorMetricId: string;
  askVsAnchorPolicy: TargetMetricAskVsAnchorPolicy;
}>;

// Asked-vs-anchor reconciliation is explicit and disclosed: a substitution the executive
// cannot see is a fabrication vector.
export type ObjectiveMetricReconciliation =
  | Readonly<{ status: 'same_metric'; message: string }>
  | Readonly<{
      status: 'anchored_substitution';
      message: string;
      visibleAnswerDisclosure: string;
    }>
  | Readonly<{ status: 'blocked'; blockedReason: string }>;

export type ObjectiveMetricResolution = Readonly<{
  askedMetric: TargetMetricRegistryEntry;
  anchorMetric: TargetMetricRegistryEntry;
  reconciliation: ObjectiveMetricReconciliation;
}>;

// ---------------------------------------------------------------------------
// Objective constraints (closed vocabulary)
// ---------------------------------------------------------------------------

// Closed set: the classifier may only PICK from these ids. Adding a constraint id is a
// contract change, not a prompt change.
export type ObjectiveConstraintId =
  | 'exclude_revenue_levers'
  | 'no_headcount'
  | 'cash_neutral'
  | 'no_capex'
  | 'no_workforce_reduction'
  | 'maintain_service_level';

export const OBJECTIVE_CONSTRAINT_IDS: readonly ObjectiveConstraintId[] = [
  'exclude_revenue_levers',
  'no_headcount',
  'cash_neutral',
  'no_capex',
  'no_workforce_reduction',
  'maintain_service_level',
];

export type ObjectiveConstraint = Readonly<{
  constraintId: ObjectiveConstraintId;
  // The executive's literal phrase — recorded for the mutation log, never re-parsed.
  sourcePhrase: string;
  status: 'satisfied' | 'violated' | 'unknown';
  enforcement: 'block' | 'withhold_lever_branch' | 'annotate';
  // Deterministic selection effect: which branches drop and what evidence is demanded.
  selectionPolicyEffect: Readonly<{
    excludedLeverBranches: readonly LeverBranchId[];
    requiredEvidenceKeys: readonly string[];
  }>;
  narrationLabel: string;
  evidenceRefIds: readonly string[];
}>;

// ---------------------------------------------------------------------------
// Objective scenario dispatch
// ---------------------------------------------------------------------------

export const OBJECTIVE_SCENARIO_DISPATCH_SCHEMA_VERSION =
  'objective_scenario_dispatch_v1';

export type ObjectiveScenarioDispatchSchemaVersion =
  typeof OBJECTIVE_SCENARIO_DISPATCH_SCHEMA_VERSION;

// Known typed arg shapes first; the Record fallback keeps new module args representable
// without a contract rev, but every invocation still names its argumentSchemaRef.
export type ObjectiveScenarioDispatchArgs =
  | Readonly<{ savingsRatePct: number }>
  | Readonly<{ revenueUpliftPct: number }>
  | Readonly<{ rifPct: number }>
  | Readonly<{ targetDeltaUsd: number }>
  | Readonly<Record<string, unknown>>;

export type ObjectiveScenarioInvocation = Readonly<{
  moduleId: string;
  toolName: string;
  intentId: string;
  // Scenario runs are always an explicit opt-in purpose — never an implicit side effect.
  runtimePurpose: 'run_scenarios';
  leverFamilyIds: readonly string[];
  args: ObjectiveScenarioDispatchArgs;
  // Where the typed bounds for `args` live — validation reads the schema, not the code.
  argumentSchemaRef: string;
  optInReason: 'objective_requires_scenario' | 'planner_verified_run_request';
}>;

export type ObjectiveScenarioDispatch = Readonly<{
  schemaVersion: ObjectiveScenarioDispatchSchemaVersion;
  matchedObjectiveText: string;
  matchPolicy: 'exact_phrase_required' | 'planner_verified';
  objective: Readonly<{
    metricId: string;
    direction: TargetMetricDirection;
    magnitude: Readonly<{ value: number; unit: TargetMetricUnit }>;
    timeframe: string;
    constraints: readonly ObjectiveConstraint[];
  }>;
  invocations: readonly ObjectiveScenarioInvocation[];
  // De-conflation fence: levers excluded from the composite carry an explicit reason.
  collapsePolicy: Readonly<{
    canonicalSourceRequired: boolean;
    excludedLeverFamilyIds: readonly string[];
    exclusionReason: string;
  }>;
  proof: Readonly<{
    routeBindingId?: string;
    plannerDecisionId?: string;
    evidenceRefIds: readonly string[];
  }>;
}>;

// ---------------------------------------------------------------------------
// Closed overlap-dimension vocabulary
// ---------------------------------------------------------------------------

// The de-confliction law: overlap is only computable over a FIXED, closed key vocabulary
// per metric family. Invented dimensions are invisible to the overlap fence and must be
// rejected, never silently accepted.
export const OVERLAP_DIMENSION_KEYS_BY_METRIC_FAMILY = {
  cost: ['costPoolIds', 'glAccountIds'],
  revenue: ['customerIds', 'laneIds', 'pricingDims'],
  cash: ['cashFlowLineIds', 'balanceSheetAccountIds'],
  returns: ['projectIds', 'capitalPoolIds'],
  margin: ['customerIds', 'laneIds', 'serviceLineIds'],
  capacity: ['siteIds', 'roleFamilyIds', 'processIds'],
} as const satisfies Readonly<Record<TargetMetricFamily, readonly string[]>>;

export type OverlapDimensionKeyByMetricFamily =
  typeof OVERLAP_DIMENSION_KEYS_BY_METRIC_FAMILY;

// Union of all 13 approved keys across every metric family.
export type ApprovedOverlapDimensionKey =
  OverlapDimensionKeyByMetricFamily[TargetMetricFamily][number];

export type OverlapVocabularyValidation = Readonly<{
  metricFamily: TargetMetricFamily;
  suppliedKeys: readonly string[];
  status: 'accepted' | 'blocked';
  invalidKeys: readonly string[];
}>;

// Fail-closed vocabulary check: any key outside the metric family's approved list blocks
// the whole set and names the offenders. No partial acceptance.
export function validateOverlapDimensionKeys(
  metricFamily: TargetMetricFamily,
  suppliedKeys: readonly string[],
): OverlapVocabularyValidation {
  const approved: readonly string[] =
    OVERLAP_DIMENSION_KEYS_BY_METRIC_FAMILY[metricFamily];
  const invalidKeys = suppliedKeys.filter((key) => !approved.includes(key));
  return {
    metricFamily,
    suppliedKeys,
    status: invalidKeys.length === 0 ? 'accepted' : 'blocked',
    invalidKeys,
  };
}

// ---------------------------------------------------------------------------
// Measure bridge (#5329)
// ---------------------------------------------------------------------------

// WHY A BRIDGE IS A CONTRACT, NOT A RULE.
//
// A contribution is packaged against an objective only when the two state the
// SAME governed measure, because adding a value measured in one measure to a
// distance stated in another is exactly the arithmetic the never-sum fences
// exist to stop. That is right, and it is also why a governed statement of the
// form "measure B is measure A plus a governed constant" cannot be inferred:
// nothing in a metric id, a label, a family, or a unit says whether two
// measures differ by a constant, so no code may decide it.
//
// So the INTENT that computes such a statement declares it
// (`GovernedCapabilityIntent.measureBridge`). It is deliberately NOT an
// `ObjectiveContributionContract`: a restatement contributes no value toward
// closing a distance, and every census a contribution passes through — composed
// -formula edges, additivity and grain, module-emitted backing — exists to keep
// module-minted VALUE honest. Declaring a restatement as a contribution would
// have put a governed level into a package that measures distance.
//
// The declaration is closed on every axis a reader's money depends on: which
// measure it starts from, which measure it states, which transform it applies,
// and which governed claim carries the constant. Nothing here is prose, nothing
// is a name to match, and the constant is never a literal — it is a claim id the
// declaring result must actually bind.

// The closed transform vocabulary. `identity_plus_constant` states
// `to = from + constant`: a contribution stated in the FROM measure moves the
// TO measure one-for-one, PROVIDED it leaves the constant unchanged (the
// consumer proves that structurally, from the contribution's own impact claims
// — it is never assumed).
export const OBJECTIVE_MEASURE_BRIDGE_TRANSFORMS = [
  'identity_plus_constant',
] as const;

export type ObjectiveMeasureBridgeTransform =
  (typeof OBJECTIVE_MEASURE_BRIDGE_TRANSFORMS)[number];

/**
 * A metric reference inside a bridge. `registryVersion` is OPTIONAL and
 * deliberately so: the bridge names the CANONICAL measure, and an unpinned
 * reference resolves through the same registry authority the validated
 * objective's own measure resolves through. A bridge that pinned an overlay
 * version would compare a measure against itself at two versions and never
 * meet.
 */
export type ObjectiveMeasureBridgeMetricRef = Readonly<{
  metricId: string;
  registryVersion?: string | null;
}>;

export type ObjectiveMeasureBridge = Readonly<{
  /** The measure a contribution may be stated in and still move `toMeasureRef`. */
  fromMeasureRef: ObjectiveMeasureBridgeMetricRef;
  /** The measure this bridge states — the measure it can serve as an objective. */
  toMeasureRef: ObjectiveMeasureBridgeMetricRef;
  transform: ObjectiveMeasureBridgeTransform;
  /** The governed numeric claim carrying the constant. Never a literal. */
  constantClaimId: string;
  /** The published binding identity of that constant. */
  constantBindingKey: string;
  /**
   * The impact-claim metric key the constant occupies. A contribution whose own
   * impact claims touch this key CHANGES the constant, so it may not ride this
   * bridge at par — the consumer holds it out under its own named reason.
   */
  constantMetricKey: ImpactClaim['metricKey'];
}>;

// ---------------------------------------------------------------------------
// Objective contribution contract
// ---------------------------------------------------------------------------

// Adopted into GovernedCapabilityIntent by the consumer lane with a declared==emitted CI
// test, so every identity field below is REQUIRED — no `?`, no `| null`. A missing claim
// id or binding key must fail at compile time, not surface as a silent non-contribution.
export type ObjectiveContributionContract = Readonly<{
  targetMetricRef: Readonly<{ metricId: string; registryVersion: string }>;
  contributionClaimId: string;
  numericBindingKey: string;
  unit: TargetMetricUnit;
  signConvention: 'positive_improves_metric' | 'negative_improves_metric';
  publishability: 'additive' | 'candidate_only' | 'non_additive' | 'cash_only';
  formulaRefs: readonly string[];
  requiredEvidenceKeys: readonly string[];
  requiredImpactClaim: Readonly<{
    metricKey: ImpactClaim['metricKey'];
    claimTypes: readonly ('p_and_l' | 'cash' | 'run_rate' | 'capacity' | 'risk')[];
    // MUST stay the closed key type, never `readonly string[]`: a prior audit proved
    // shape-mismatched overlap keys let identical cost pools slip past the
    // de-confliction fence (silent double-count). The closed type makes that
    // structurally impossible.
    overlapDimensionKeys: readonly ApprovedOverlapDimensionKey[];
  }>;
}>;

// Fails closed when a contract carries overlap keys outside the vocabulary of the
// registry entry's metric family (e.g. a cost contract carrying 'customerIds').
export function validateContributionContractOverlapKeys(
  contract: ObjectiveContributionContract,
  registryEntry: TargetMetricRegistryEntry,
): OverlapVocabularyValidation {
  return validateOverlapDimensionKeys(
    registryEntry.metricFamily,
    contract.requiredImpactClaim.overlapDimensionKeys,
  );
}

// ---------------------------------------------------------------------------
// Objective value-bridge picture
// ---------------------------------------------------------------------------

export const OBJECTIVE_VALUE_BRIDGE_PICTURE_SCHEMA_VERSION =
  'objective_value_bridge_picture_v1';

export type ObjectiveValueBridgePictureSchemaVersion =
  typeof OBJECTIVE_VALUE_BRIDGE_PICTURE_SCHEMA_VERSION;

// Same three tiers as the composite EBITDA aggregator's lever results, generalized.
export type ObjectiveBridgeLeverTier =
  | 'governed'
  | 'strategic_advisory'
  | 'unlockable';

// A priced lever row. Generalizes CompositeEbitdaLeverResult: the value is metric-agnostic
// (value + unit + binding key) instead of a hardcoded `ebitdaContributionUsd`.
export type ObjectiveBridgeLever = Readonly<{
  leverFamilyId: string;
  // Null when the lever is advisory and no module produced the row.
  moduleId: string | null;
  label: string;
  tier: ObjectiveBridgeLeverTier;
  // Null = present in the package narrative but not publishable as a number.
  contributionValue: number | null;
  unit: TargetMetricUnit;
  // Every published value must resolve through a NumericBinding — no naked numbers.
  numericBindingKey: string;
  // Signed amount already netted out of contributionValue by de-confliction (0 = none).
  deconflictionAdjustmentValue: number;
  contributionClaimIds: readonly string[];
  evidenceRefIds: readonly string[];
}>;

// A withheld lever must carry the honest reason AND what would unlock it — honest
// withhold is a correct answer, not a failure state.
export type ObjectiveBridgeWithheldLever = Readonly<{
  leverFamilyId: string;
  moduleId: string | null;
  label: string;
  tier: ObjectiveBridgeLeverTier;
  // The narrator-facing honest reason; never softened downstream.
  withheldReason: string;
  // What evidence/receipt would move this lever into the package (null = structural).
  unlockReason: string | null;
  // Why the value cannot be added to the composite (overlap without receipt, etc.).
  nonSummableReason: string | null;
  requiredEvidenceKeys: readonly string[];
}>;

// Generalizes CompositeEbitdaDeconflictionAdjustment: metric-agnostic amount + the closed
// overlap keys the adjustment was computed over (receipt of the de-confliction itself).
export type DeconflictionAdjustment = Readonly<{
  leverFamilyId: string;
  moduleId: string;
  claimId: string;
  adjustmentValue: number;
  unit: TargetMetricUnit;
  overlapDimensionKeys: readonly ApprovedOverlapDimensionKey[];
  evidenceRefIds: readonly string[];
  reason: string;
}>;

// Minimal narration steering only — hints label bound facts, they never carry numbers.
export type ObjectiveBridgeNarrationHints = Readonly<{
  confidenceStatement: string;
  controlsStatement: string;
  headlineLabelByBindingKey?: Readonly<Record<string, string>>;
}>;

// Anchor is either fully evidence-backed (all four numbers + receipt trail) or blocked
// with the missing evidence named — a blocked anchor carries NO dollar values.
export type ObjectiveBridgeAnchor =
  | Readonly<{
      baselineValue: number;
      targetValue: number;
      targetDelta: number;
      unit: TargetMetricUnit;
      evidenceRefIds: readonly string[];
      asOf: string;
      formulaRef: string;
    }>
  | Readonly<{
      blockedReason: string;
      missingEvidenceKeys: readonly string[];
    }>;

export type ObjectiveValueBridgePicture = Readonly<{
  schemaVersion: ObjectiveValueBridgePictureSchemaVersion;
  generatedAt: string;
  objective: Readonly<{
    objectiveId?: string;
    metricResolution: ObjectiveMetricResolution;
    direction: TargetMetricDirection;
    targetPct?: number;
    timeframe: string;
    scope: string;
    constraints: readonly ObjectiveConstraint[];
    scenarioDispatch?: ObjectiveScenarioDispatch;
  }>;
  anchor: ObjectiveBridgeAnchor;
  package: Readonly<{
    // Governed, de-conflicted package value; null = not publishable (withhold).
    approvedPackageValue: number | null;
    remainingGapValue: number | null;
    // Canonical narration metric ("targetFill", never "coverage" — the coverage
    // vocabulary produced the "100% covered" overclaim).
    targetFillPct: number | null;
    // True only when every lever is receipted + non-overlapping (fully summable).
    canPublishFullCompositeTotal: boolean;
    fullCompositeValue: number | null;
    selectedLeverFamilyIds: readonly string[];
    selectionPolicy: string | null;
  }>;
  levers: readonly ObjectiveBridgeLever[];
  withheldLevers: readonly ObjectiveBridgeWithheldLever[];
  deconfliction: Readonly<{
    status: 'complete' | 'partial' | 'blocked';
    overlapDimensionKeys: readonly string[];
    adjustments: readonly DeconflictionAdjustment[];
    // Lever-family pairs with unknown/unreceipted overlap; non-empty forces partial.
    unresolvedPairs: readonly string[];
  }>;
  evidenceRefs: readonly GovernedToolOutputEvidenceRef[];
  numericBindings: readonly NumericBinding[];
  narrationHints: ObjectiveBridgeNarrationHints;
}>;
